import React, { Component } from "react";
import { calcNights, api } from "../../utils/api";
import { Search, X, MapPin, Phone, ChevronDown, RefreshCw, Plus, Minus } from "lucide-react";

const GST_RATES = [0, 5, 12, 18, 28];

const MEAL_PLANS = [
  { code: 'EP',  desc: 'Room Only'               },
  { code: 'CP',  desc: 'Breakfast'               },
  { code: 'MAP', desc: 'Breakfast & Lunch/Dinner' },
  { code: 'AP',  desc: 'All Meals'               },
];

const ROOM_TYPES_FALLBACK = [
  "Standard Room","Superior Room","Deluxe Room","Premium Room","Luxury Room",
  "Junior Suite","Suite","Executive Suite","Presidential Suite","Studio Room",
  "Executive Room","Club Room","Garden Room","Pool View Room","Sea View Room",
  "Mountain View Room","Villa","Pool Villa","Cottage","Bungalow","Chalet",
  "Family Room","Connecting Room","Triple Room","Quad Room","Dormitory","Tent","Treehouse",
];

function calcRoomTotals(booking, nights) {
  const qty        = parseInt(booking.count) || 0;
  const n          = parseInt(nights) || 0;
  const units      = qty * n;
  const tacRoom    = parseFloat(booking.tac_on_room) || 0;
  const purBase    = units * (parseFloat(booking.purchase_price) || 0);  // gross — TAC not included
  const saleBase   = units * (parseFloat(booking.sale_price)     || 0);
  const purGstPct  = parseFloat(booking.purchase_gst_rate) || 0;
  const saleGstPct = parseFloat(booking.gst_rate)          || 0;
  const purGst     = purBase  * (purGstPct  / 100);          // GST on the gross base
  const saleGst    = saleBase * (saleGstPct / 100);
  return {
    units,
    purBase,  purGst,  purTotal: purBase + purGst,  // display: Sub + GST only, TAC not subtracted here
    saleBase, saleGst, saleTotal: saleBase + saleGst,
    purGstPct, saleGstPct,
    tacDeduction: tacRoom,
  };
}

function calcExtraBedTotals(booking, nights) {
  const n         = parseInt(nights) || 0;
  const tacExBed  = parseFloat(booking.tac_on_ex_bed) || 0;
  // Child extra bed (no TAC on child)
  const ebcCount  = parseInt(booking.extra_bed_child) || 0;
  const ebcGstPct = parseFloat(booking.extra_bed_child_gst) || 0;
  const ebcPur    = ebcCount * n * (parseFloat(booking.ex_bed_rate_child_purchase) || 0);
  const ebcSale   = ebcCount * n * (parseFloat(booking.ex_bed_rate_child_sale)     || 0);
  const ebcPurGst = ebcPur  * (ebcGstPct / 100);
  const ebcSaleGst= ebcSale * (ebcGstPct / 100);
  // Adult extra bed — TAC is a flat, single deduction from (gross purchase + GST)
  const ebaCount  = parseInt(booking.extra_bed_adult) || 0;
  const ebaGstPct = parseFloat(booking.extra_bed_adult_gst) || 0;
  const ebaPur    = ebaCount * n * (parseFloat(booking.ex_bed_rate_purchase) || 0);  // gross — TAC not included
  const ebaSale   = ebaCount * n * (parseFloat(booking.ex_bed_rate_sale)   || 0);
  const ebaPurGst = ebaPur  * (ebaGstPct / 100);
  const ebaSaleGst= ebaSale * (ebaGstPct / 100);
  const purBase  = ebcPur   + ebaPur;
  const purGst   = ebcPurGst+ ebaPurGst;
  const purTotal = purBase + purGst;  // display: Sub + GST only, TAC not subtracted here
  const saleBase = ebcSale  + ebaSale;
  const saleGst  = ebcSaleGst + ebaSaleGst;
  return {
    purBase,  purGst,  purTotal,
    saleBase, saleGst, saleTotal: saleBase + saleGst,
    hasAny: ebcCount + ebaCount > 0,
    tacDeduction: tacExBed,
  };
}

function calcBookingFullTotals(rtBookings, nights) {
  let purBase = 0, purGst = 0, purExtra = 0, saleBase = 0, saleGst = 0, saleExtra = 0, tacTotal = 0;
  for (const b of rtBookings) {
    const r  = calcRoomTotals(b, nights);
    const eb = calcExtraBedTotals(b, nights);
    purBase   += r.purBase   + eb.purBase;
    purGst    += r.purGst    + eb.purGst;
    purExtra  += parseFloat(b.extras_amount_purchase) || 0;
    saleBase  += r.saleBase  + eb.saleBase;
    saleGst   += r.saleGst   + eb.saleGst;
    saleExtra += parseFloat(b.extras_amount_sale) || 0;
    tacTotal  += (r.tacDeduction || 0) + (eb.tacDeduction || 0);
  }
  return {
    purBase, purGst, purExtra,  purTotal: purBase + purGst + purExtra, tacTotal,
    purTotalNetTac: Math.max(0, purBase + purGst + purExtra - tacTotal),
    saleBase, saleGst, saleExtra, saleTotal: saleBase + saleGst + saleExtra,
    grossProfit: (() => { const base = saleBase - purBase; return base < 0 ? base + tacTotal : base - tacTotal; })(),
  };
}

function makeInstanceId(roomTypeId) {
  return `${roomTypeId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function makeDefaultRoomBooking(roomType) {
  return {
    instance_id: makeInstanceId(roomType.id),
    room_type_id: roomType.id, room_name: roomType.room_name, count: 1,
    occupancy_type: '', no_of_persons: '', no_of_adults: '', no_of_children: '', meal_type: '',
    purchase_price: '', sale_price: '', gst_rate: 0, purchase_gst_rate: 0,
    extra_bed_child: 0, ex_bed_rate_child_purchase: 0, ex_bed_rate_child_sale: 0, extra_bed_child_gst: 0,
    extra_bed_adult: 0, ex_bed_rate_purchase: 0, ex_bed_rate_sale: 0, extra_bed_adult_gst: 0,
    tac_on_room: 0, tac_on_ex_bed: 0, room_tac_passed: 0, ex_bed_tac_passed: 0,
    extras_amount_purchase: 0, extras_amount_sale: 0, extras_details_purchase: '', extras_details_sale: '',
  };
}

class HotelDetails extends Component {
  constructor(props) {
    super(props);
    this._ktsCCSelected = [];   // sync ref — always up-to-date without waiting for setState
    this._hotelCC       = '';   // sync ref for hotel CC (selectedCcEmail) — avoids async state lag
    this.state = {
      hotels: [], filtered: [],
      searchText: props.formData.hotel_name || '',
      showDropdown: false, loading: false,
      roomTypes: [], loadingRooms: false, roomTypesError: false,
      hotelEmails: [], selectedToEmail: '', selectedCcEmail: '',
      extraCcInput: '', showExtraCcInput: false,
      vendorAgents: [],
      ktsCC: [],
      ktsCCSelected: [],
    };
    this.wrapperRef = React.createRef();
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.refs_map = {
      hotel_name: React.createRef(), check_in: React.createRef(),
      check_out: React.createRef(), destination: React.createRef(),
      mode: React.createRef(), vendor_agent_id: React.createRef(),
      room_type_bookings: React.createRef(), negative_reason: React.createRef(),
      no_rooms_warning: React.createRef(),
    };
    // Per-instance refs for room cards — keyed by instance_id, populated during
    // render via a callback ref, so scroll-to-error can target the EXACT room
    // instance that has a validation error, not just the whole section.
    this.instanceRefs = {};
  }

  componentDidMount() {
    this.fetchHotels();
    this.fetchVendorAgents();
    this.fetchKTSCC();
    document.addEventListener('mousedown', this.handleOutsideClick);
    // Re-fetch room types if a hotel was already selected (e.g. navigating back to this step)
    const vendorId = this.props.formData.purchase_from_vendor_id;
    if (vendorId) {
      this.fetchRoomTypesForVendor(vendorId);
    }
  }
  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick);
  }
  handleOutsideClick(e) {
    if (this.wrapperRef.current && !this.wrapperRef.current.contains(e.target))
      this.setState({ showDropdown: false });
  }

  async fetchHotels() {
    this.setState({ loading: true });
    try {
      const { data } = await api.get('/vendors', { params: { vendor_type: 'Hotel', is_active: 1, limit: 500 } });
      this.setState({ hotels: data.data || [], filtered: data.data || [] });
    } catch (e) { console.error('Failed to load hotels:', e.message); }
    finally { this.setState({ loading: false }); }
  }

  async fetchVendorAgents() {
    try {
      const { data } = await api.get('/agents', { params: { is_active: 1, limit: 500 } });
      const all = data.data || [];
      this.setState({ vendorAgents: all.filter(a => a.agent_type === 'Seller' || a.agent_type === 'Both' || !a.agent_type) });
    } catch (e) { console.error('Failed to load vendor agents:', e.message); }
  }

  async fetchKTSCC() {
    try {
      const { data } = await api.get('/cc-contacts', { params: { is_active: 1 } });
      const contacts = data.data || [];
      console.log(`[KTS CC] Loaded ${contacts.length} contacts:`, contacts.map(c => c.email));
      // Pre-select the logged-in user's own entry in the KTS CC list, if found.
      const myEmail = (this.props.user?.email || '').trim().toLowerCase();
      const mine = myEmail ? contacts.find(c => (c.email || '').trim().toLowerCase() === myEmail) : null;
      const defaultSelected = mine ? [mine.email] : [];
      this._ktsCCSelected = defaultSelected;
      this.setState({ ktsCC: contacts, ktsCCSelected: defaultSelected }, () => {
        this._mergeKTSCC(this.state.selectedCcEmail);
      });
    } catch (e) {
      console.error('[KTS CC] Failed to load — check app.js: app.use("/api/cc-contacts", require("./routes/ccRoutes"))', e.message);
    }
  }

  // Re-merge whenever hotel_email_to is set (hotel selected) and KTS CC is loaded
  componentDidUpdate(prevProps) {
    const { errors, formData } = this.props;
    // Auto-merge when hotel is selected and KTS CC is already available
    if (prevProps.formData.hotel_name !== formData.hotel_name && formData.hotel_name && this._ktsCCSelected.length > 0) {
      this._mergeKTSCC(this.state.selectedCcEmail);
    }
    // Scroll to error
    if (errors && errors !== prevProps.errors) {
      // Per-room-instance errors take priority — scroll to the exact room card
      // that has the missing field, not just the top of the whole section.
      if (errors.room_type_errors && errors.room_type_errors.length > 0) {
        const rtBookings = formData.room_type_bookings || [];
        const firstErr   = errors.room_type_errors[0];
        const target     = rtBookings.find(b => b.room_name === firstErr.room_name);
        const el         = target && this.instanceRefs[target.instance_id];
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
      }
      // "No rooms added at all" — scroll to the actual warning text, not the
      // top of the (very tall) room-types list container.
      if (errors.room_type_bookings && (formData.room_type_bookings || []).length === 0 && this.refs_map.no_rooms_warning?.current) {
        this.refs_map.no_rooms_warning.current.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      const ORDER = ["hotel_name","destination","check_in","check_out","mode","vendor_agent_id","room_type_bookings","negative_reason"];
      for (const key of ORDER) {
        if (errors[key] && this.refs_map[key]?.current) {
          const el = this.refs_map[key].current;
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          try { el.focus(); } catch (_) {}
          break;
        }
      }
    }
  }

  // Helper: merge hotel CC + KTS CC ref → hotel_email_cc
  // Pass hotelCcOverride when called right after setState (avoids async state lag)
  _mergeKTSCC(hotelCcOverride) {
    const isEmail = e => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
    // Use override if given, otherwise use the sync instance var _hotelCC
    const hotelCcStr = hotelCcOverride !== undefined ? hotelCcOverride : this._hotelCC;
    const hotelCc    = hotelCcStr.split(',').map(s => s.trim()).filter(isEmail);
    const ktsCc      = (this._ktsCCSelected || []).filter(isEmail);
    const merged     = [...new Set([...hotelCc, ...ktsCc])].join(', ');
    console.log('[CC Merge] hotelCc:', hotelCc, '| ktsCc:', ktsCc, '| merged:', merged);
    this.props.onChange('hotel_email_cc', merged);
  }

  handleSearchInput(e) {
    const q = e.target.value;
    this.setState({ searchText: q, showDropdown: true });
    if (!q.trim()) { this.setState({ filtered: this.state.hotels }); return; }
    const lower = q.toLowerCase();
    this.setState({ filtered: this.state.hotels.filter(h =>
      (h.vendor_name || '').toLowerCase().includes(lower) ||
      (h.city || '').toLowerCase().includes(lower) ||
      (h.billing_state || '').toLowerCase().includes(lower)
    )});
  }

  async fetchRoomTypesForVendor(vendorId) {
    this.setState({ loadingRooms: true, roomTypesError: false });
    try {
      const { data } = await api.get(`/vendors/${vendorId}/room-types`);
      const rooms = data.data || [];
      if (rooms.length > 0) {
        this.setState({ roomTypes: rooms, loadingRooms: false, roomTypesError: false });
      } else {
        this.setState({ roomTypes: ROOM_TYPES_FALLBACK.map((r, i) => ({ id: `s${i}`, room_name: r, max_adults: 2, max_children: 1 })), loadingRooms: false, roomTypesError: false });
      }
    } catch (e) {
      this.setState({ roomTypes: ROOM_TYPES_FALLBACK.map((r, i) => ({ id: `s${i}`, room_name: r, max_adults: 2, max_children: 1 })), loadingRooms: false, roomTypesError: false });
    }
  }

  async handleSelectHotel(hotel) {
    const destination = [hotel.city, hotel.billing_state].filter(Boolean).join(', ');
    this.props.onChange('hotel_name', hotel.vendor_name);
    this.props.onChange('destination', destination);
    this.props.onChange('purchase_from_vendor_id', hotel.id);
    this.props.onChange('purchase_from', hotel.vendor_name);
    this.props.onChange('vendor_type', this.props.formData.vendor_type || 'Direct');
    this.props.onChange('room_type', '');
    this.props.onChange('room_type_bookings', []);
    if (hotel.billing_state) this.props.onChange('billing_state_purchase', hotel.billing_state);
    this.setState({ searchText: hotel.vendor_name, showDropdown: false, roomTypes: [], loadingRooms: true, roomTypesError: false, hotelEmails: [], selectedToEmail: '', selectedCcEmail: '' }); this._hotelCC = '';
    const hotelEmails = (hotel.email || '').split('//').map(e => e.trim()).filter(Boolean);
    this.setState({ hotelEmails });
    if (hotelEmails.length > 0) {
      this.setState({ selectedToEmail: hotelEmails[0] });
      this.props.onChange('hotel_email_to', hotelEmails[0]);
      // Reset hotel CC but keep KTS CC
      const ktsEmails = this._ktsCCSelected || [];
      this.props.onChange('hotel_email_cc', ktsEmails.join(', '));
    }
    this.fetchRoomTypesForVendor(hotel.id);
  }

  handleClearHotel() {
    ['hotel_name','destination','room_type','hotel_email_to','vendor_agent_name'].forEach(k => this.props.onChange(k, ''));
    ['purchase_from_vendor_id','vendor_agent_id'].forEach(k => this.props.onChange(k, null));
    this.props.onChange('room_type_bookings', []);
    this.props.onChange('vendor_type', 'Direct');
    this.props.onChange('purchase_from', '');
    // Keep KTS CC, clear only hotel CC
    const ktsEmails = this._ktsCCSelected || [];
    this.props.onChange('hotel_email_cc', ktsEmails.join(', '));
    this.setState({ searchText: '', filtered: this.state.hotels, showDropdown: true, roomTypes: [], roomTypesError: false, hotelEmails: [], selectedToEmail: '', selectedCcEmail: '', extraCcInput: '', showExtraCcInput: false }); this._hotelCC = '';
  }

  getRoomBookingsForType(roomTypeId) {
    return (this.props.formData.room_type_bookings || []).filter(b => b.room_type_id === roomTypeId);
  }

  addRoomInstance(roomType) {
    const list = [...(this.props.formData.room_type_bookings || []), makeDefaultRoomBooking(roomType)];
    this.props.onChange('room_type_bookings', list);
  }

  removeRoomInstance(instanceId) {
    const list = (this.props.formData.room_type_bookings || []).filter(b => b.instance_id !== instanceId);
    this.props.onChange('room_type_bookings', list);
    delete this.instanceRefs[instanceId];
  }

  setField(instanceId, field, value) {
    const list = [...(this.props.formData.room_type_bookings || [])];
    const idx  = list.findIndex(b => b.instance_id === instanceId);
    if (idx === -1) return;

    const updated = { ...list[idx], [field]: value };

    // ── Auto-calculate no_of_adults and no_of_children separately ────────────
    if (field === 'occupancy_type' || field === 'extra_bed_child' || field === 'extra_bed_adult') {
      const occ         = updated.occupancy_type || '';
      const childBeds   = parseInt(updated.extra_bed_child) || 0;
      const adultBeds   = parseInt(updated.extra_bed_adult) || 0;
      const basePersons = occ === 'Single Occupancy' ? 1 : occ === 'Double' ? 2 : occ === 'Triple' ? 3 : 0;
      if (basePersons > 0 || occ) {
        updated.no_of_adults   = basePersons + adultBeds;
        updated.no_of_children = childBeds;
        updated.no_of_persons  = basePersons + adultBeds + childBeds;   // keep for backend
      }
    }

    list[idx] = updated;
    this.props.onChange('room_type_bookings', list);
  }

  handleChange(e) {
    const { name, value, type } = e.target;
    const parsed = type === 'number' ? (value === '' ? '' : parseFloat(value) || 0) : value;
    this.props.onChange(name, parsed);
    if (name === 'extra_bed_child' || name === 'extra_bed_adult') {
      const child = name === 'extra_bed_child' ? (parseFloat(value)||0) : (parseFloat(this.props.formData.extra_bed_child)||0);
      const adult = name === 'extra_bed_adult' ? (parseFloat(value)||0) : (parseFloat(this.props.formData.extra_bed_adult)||0);
      this.props.onChange('extra_bed', child + adult);
    }
    if (name === 'check_in' || name === 'check_out') {
      const checkIn  = name === 'check_in'  ? value : this.props.formData.check_in;
      const checkOut = name === 'check_out' ? value : this.props.formData.check_out;
      const nights = calcNights(checkIn, checkOut);
      if (nights > 0) { this.props.onChange('no_of_nights', nights); this.props.onChange('total_nights', nights); }
    }
  }

  handleBlur(e) {
    const { name, value, type } = e.target;
    if (type === 'number' && value === '') this.props.onChange(name, 0);
  }

  render() {
    const { formData, errors } = this.props;
    const { searchText, filtered, showDropdown, loading, roomTypes, loadingRooms, roomTypesError, hotelEmails, selectedToEmail, selectedCcEmail, extraCcInput, showExtraCcInput, ktsCC, ktsCCSelected } = this.state;

    const isSelected   = !!formData.hotel_name;
    const nights       = parseInt(formData.no_of_nights) || 0;
    const rtBookings   = formData.room_type_bookings || [];
    const totalRooms   = rtBookings.reduce((s, b) => s + (b.count || 0), 0);
    const fullTotals   = calcBookingFullTotals(rtBookings, nights);
    const isFullInput   = formData.mode === 'Full Input';
    // Display totals (top summary strip): Sub + GST only, TAC not subtracted here.
    // TAC is still netted into the real Gross Profit below — just not shown as
    // part of "Total Purchase" on screen.
    const grandPurchase = fullTotals.purBase;
    const grandPurGst   = fullTotals.purGst;
    const grandPurTotal = fullTotals.purTotal;
    const grandSaleBase = fullTotals.saleBase;
    const grandGST      = fullTotals.saleGst;
    const grandTotal    = fullTotals.saleTotal;
    // Both modes: ex-GST margin, TAC added back when the pre-TAC margin is
    // already negative (softens the loss), subtracted otherwise. Service
    // Charge no longer includes GST in Gross Profit — same rule as Full Input.
    const gpBaseFull    = (fullTotals.saleBase + fullTotals.saleExtra) - (fullTotals.purBase + fullTotals.purExtra);
    const grossProfit   = gpBaseFull < 0 ? gpBaseFull + fullTotals.tacTotal : gpBaseFull - fullTotals.tacTotal;
    const gpNegative    = grossProfit < 0 && rtBookings.length > 0 && grandSaleBase > 0;

    return (
      <div className="space-y-6">
        <section>
          <h3 className="section-title">Hotel Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* ── Hotel Name ── */}
            <div className="field-group md:col-span-2" ref={this.wrapperRef} style={{ position: 'relative' }}>
              <label>Hotel Name *</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <Search size={14} style={{ position: 'absolute', left: 12, color: '#9CA3AF', zIndex: 1, pointerEvents: 'none' }} />
                <input ref={this.refs_map.hotel_name} type="text"
                  placeholder={loading ? 'Loading hotels...' : 'Search hotel name, city or state...'}
                  value={searchText} onChange={this.handleSearchInput.bind(this)}
                  onFocus={() => this.setState({ showDropdown: true })}
                  style={{ paddingLeft: 36, paddingRight: isSelected ? 60 : 36, width: '100%', ...(errors.hotel_name ? { borderColor: '#DC2626', boxShadow: '0 0 0 2px rgba(220,38,38,0.15)' } : {}) }}
                  autoComplete="off" />
                <ChevronDown size={14} style={{ position: 'absolute', right: isSelected ? 34 : 10, color: '#9CA3AF', pointerEvents: 'none', transition: 'transform 0.2s', transform: showDropdown ? 'rotate(180deg)' : 'none' }} />
                {isSelected && (
                  <button type="button" onClick={this.handleClearHotel.bind(this)}
                    style={{ position: 'absolute', right: 10, background: '#F3F4F6', border: 'none', borderRadius: 6, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6B7280' }}>
                    <X size={12} />
                  </button>
                )}
              </div>
              {errors.hotel_name && <span className="error">{errors.hotel_name}</span>}
              {isSelected && (
                <div style={{ marginTop: 6, padding: '8px 12px', background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 10, fontSize: 12 }}>
                  {formData.destination && <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#0369A1' }}><MapPin size={11} /> {formData.destination}</span>}
                </div>
              )}
              {isSelected && hotelEmails.length === 0 && (
                <div style={{ marginTop: 8, padding: '8px 12px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 11, color: '#92400E' }}>
                  ⚠️ No email on file for this hotel. Add one via <strong>Hotels → Edit</strong>.
                </div>
              )}
              {showDropdown && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', marginTop: 4, maxHeight: 280, overflowY: 'auto' }}>
                  {filtered.length === 0 ? (
                    <div style={{ padding: 16, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>{loading ? 'Loading...' : 'No hotels found'}</div>
                  ) : (
                    <>
                      <div style={{ padding: '8px 12px 4px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9CA3AF', borderBottom: '1px solid #F3F4F6' }}>
                        {filtered.length} hotel{filtered.length !== 1 ? 's' : ''}
                      </div>
                      {filtered.map(h => (
                        <div key={h.id} onClick={() => this.handleSelectHotel(h)}
                          style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #F9FAFB', background: formData.hotel_name === h.vendor_name ? '#EFF6FF' : 'transparent' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F8FAFF'}
                          onMouseLeave={e => e.currentTarget.style.background = formData.hotel_name === h.vendor_name ? '#EFF6FF' : 'transparent'}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{h.vendor_name}</div>
                          <div style={{ display: 'flex', gap: 10, marginTop: 3 }}>
                            {h.city && <span style={{ fontSize: 11, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={10} /> {h.city}{h.billing_state ? `, ${h.billing_state}` : ''}</span>}
                            {h.hotel_no && <span style={{ fontSize: 11, color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 3 }}><Phone size={10} /> {h.hotel_no}</span>}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>


            {/* ── Hotel Confirmation Email — full width ── */}
            {isSelected && hotelEmails.length > 0 && (
              <div style={{ gridColumn: '1 / -1' }}>

                <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: '14px 20px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#065F46', marginBottom: 10 }}>📧 Hotel Confirmation Email</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', display: 'block', marginBottom: 4 }}>TO (Primary) *</label>
                      <select value={selectedToEmail}
                        onChange={e => {
                          const val = e.target.value;
                          this.setState({ selectedToEmail: val });
                          this.props.onChange('hotel_email_to', val);
                          const newCc = (selectedCcEmail || '').split(',').map(s => s.trim()).filter(s => s && s !== val);
                          const newCcJoined = newCc.join(', ');
                          this._hotelCC = newCcJoined; this.setState({ selectedCcEmail: newCcJoined });
                          this._mergeKTSCC(newCcJoined);
                        }}
                        style={{ width: '100%', border: '1.5px solid #BBF7D0', borderRadius: 8, padding: '8px 10px', fontSize: 13, background: '#fff', color: '#111827' }}>
                        <option value="">Select email</option>
                        {hotelEmails.map((e, i) => <option key={i} value={e}>{e}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', display: 'block', marginBottom: 4 }}>CC (Multiple allowed)</label>
                      <div style={{ border: '1.5px solid #D1D5DB', borderRadius: 8, padding: '6px 10px', background: '#fff', display: 'flex', flexDirection: 'column', gap: 5, minHeight: 38 }}>
                        {hotelEmails.filter(e => e !== selectedToEmail).map((e, i) => {
                          const ccList  = (selectedCcEmail || '').split(',').map(s => s.trim()).filter(Boolean);
                          const checked = ccList.includes(e);
                          return (
                            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12, color: '#374151' }}>
                              <input type="checkbox" checked={checked} onChange={ev => {
                                let newCc = ccList.filter(s => s !== e);
                                if (ev.target.checked) newCc.push(e);
                                const joined = newCc.join(', ');
                                this._hotelCC = joined; this.setState({ selectedCcEmail: joined });
                                this._mergeKTSCC(joined);
                              }} style={{ accentColor: '#1E2D4F', width: 14, height: 14 }} />
                              <span style={{ wordBreak: 'break-all' }}>{e}</span>
                            </label>
                          );
                        })}
                        {(selectedCcEmail || '').split(',').map(s => s.trim()).filter(s => s && !hotelEmails.includes(s)).map((e, i) => (
                          <label key={`extra-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#374151' }}>
                            <input type="checkbox" checked readOnly onChange={() => {
                              const newCc = (selectedCcEmail || '').split(',').map(s => s.trim()).filter(s => s && s !== e);
                              const joined = newCc.join(', ');
                              this._hotelCC = joined; this.setState({ selectedCcEmail: joined });
                              this._mergeKTSCC(joined);
                            }} style={{ accentColor: '#1E2D4F', width: 14, height: 14 }} />
                            <span style={{ wordBreak: 'break-all', flex: 1 }}>{e}</span>
                            <button type="button" onClick={() => {
                              const newCc = (selectedCcEmail || '').split(',').map(s => s.trim()).filter(s => s && s !== e);
                              const joined = newCc.join(', ');
                              this._hotelCC = joined; this.setState({ selectedCcEmail: joined });
                              this._mergeKTSCC(joined);
                            }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 14, padding: '0 2px', lineHeight: 1 }}>×</button>
                          </label>
                        ))}
                        {hotelEmails.filter(e => e !== selectedToEmail).length === 0 && (selectedCcEmail || '').split(',').filter(s => s.trim()).length === 0 && (
                          <span style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>No other emails</span>
                        )}
                        {!showExtraCcInput && (
                          <button type="button" onClick={() => this.setState({ showExtraCcInput: true, extraCcInput: '' })}
                            style={{ alignSelf: 'flex-start', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4, background: '#EEF2FF', border: '1px dashed #818CF8', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, color: '#4338CA', cursor: 'pointer' }}>
                            + Add CC email
                          </button>
                        )}
                        {showExtraCcInput && (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                            <input type="email" autoFocus placeholder="email@example.com" value={extraCcInput}
                              onChange={e => this.setState({ extraCcInput: e.target.value })}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const val = extraCcInput.trim();
                                  if (val && val.includes('@')) {
                                    const existing = (selectedCcEmail || '').split(',').map(s => s.trim()).filter(Boolean);
                                    if (!existing.includes(val)) {
                                      const joined = [...existing, val].join(', ');
                                      this._hotelCC = joined; this.setState({ selectedCcEmail: joined, extraCcInput: '', showExtraCcInput: false });
                                      this._mergeKTSCC(joined);
                                    }
                                  }
                                }
                                if (e.key === 'Escape') this.setState({ showExtraCcInput: false, extraCcInput: '' });
                              }}
                              style={{ flex: 1, height: 28, border: '1.5px solid #818CF8', borderRadius: 6, padding: '0 8px', fontSize: 12, outline: 'none', fontFamily: 'Sora,sans-serif' }} />
                            <button type="button" onClick={() => {
                              const val = extraCcInput.trim();
                              if (val && val.includes('@')) {
                                const existing = (selectedCcEmail || '').split(',').map(s => s.trim()).filter(Boolean);
                                if (!existing.includes(val)) {
                                  const joined = [...existing, val].join(', ');
                                  this._hotelCC = joined; this.setState({ selectedCcEmail: joined, extraCcInput: '', showExtraCcInput: false });
                                  this._mergeKTSCC(joined);
                                }
                              }
                            }} style={{ height: 28, padding: '0 10px', background: '#4338CA', border: 'none', borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Add</button>
                            <button type="button" onClick={() => this.setState({ showExtraCcInput: false, extraCcInput: '' })}
                              style={{ height: 28, padding: '0 8px', background: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: 6, color: '#6B7280', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* ── KTS CC column (3rd) ── */}
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', display: 'block', marginBottom: 4 }}>CC from KTS</label>
                      <div style={{ border: '1.5px solid #D1D5DB', borderRadius: 8, padding: '6px 10px', background: '#fff', display: 'flex', flexDirection: 'column', gap: 5, minHeight: 38, maxHeight: 130, overflowY: 'auto' }}>
                        {ktsCC.length === 0 ? (
                          <span style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>No KTS contacts</span>
                        ) : ktsCC.map(c => {
                          const checked = ktsCCSelected.includes(c.email);
                          return (
                            <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12, color: '#374151' }}>
                              <input type="checkbox" checked={checked} onChange={() => {
                                const newSel = checked
                                  ? ktsCCSelected.filter(e => e !== c.email)
                                  : [...ktsCCSelected, c.email];
                                console.log('[KTS CC Toggle] email:', c.email, '| checked was:', checked, '| newSel:', newSel);
                                this._ktsCCSelected = newSel;
                                console.log('[KTS CC Toggle] _ktsCCSelected is now:', this._ktsCCSelected);
                                this.setState({ ktsCCSelected: newSel });
                                this._mergeKTSCC();
                              }} style={{ accentColor: '#1E2D4F', width: 14, height: 14, flexShrink: 0 }} />
                              <div style={{ lineHeight: 1.3 }}>
                                <div style={{ fontWeight: 700, fontSize: 12, color: '#111827' }}>{c.name}</div>
                                <div style={{ fontSize: 10, color: '#6B7280', wordBreak: 'break-all' }}>{c.email}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  {selectedToEmail && (
                    <p style={{ fontSize: 11, color: '#059669', marginTop: 6 }}>
                      ✓ To: <strong>{selectedToEmail}</strong>
                      {selectedCcEmail && <> | CC: <strong>{selectedCcEmail}</strong></>}
                      {ktsCCSelected.length > 0 && <> | KTS CC: <strong>{ktsCCSelected.length} contact{ktsCCSelected.length!==1?'s':''}</strong></>}
                    </p>
                  )}
                </div>
              </div>
            )}
            {/* ── Destination ── */}
            <div className="field-group">
              <label>Destination *</label>
              <input type="text" name="destination" placeholder="Auto-filled from hotel selection"
                value={formData.destination || ''} onChange={this.handleChange.bind(this)}
                style={{ background: formData.destination ? '#F0FFF4' : undefined }} />
              {formData.destination && <p style={{ fontSize: 11, color: '#059669', marginTop: 3 }}>✓ Auto-filled from hotel</p>}
              {errors.destination && <span className="error">{errors.destination}</span>}
            </div>

            {/* ── Vendor Type ── */}
            {isSelected && (
              <div className="field-group" ref={this.refs_map.vendor_agent_id} style={{ gridColumn: '1 / -1' }}>
                <label style={{ marginBottom: 6, display: 'block' }}>Vendor Type *</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {['Direct', 'Vendor'].map(vt => {
                    const active = (formData.vendor_type || 'Direct') === vt;
                    return (
                      <button key={vt} type="button"
                        onClick={() => {
                          this.props.onChange('vendor_type', vt);
                          if (vt === 'Direct') {
                            this.props.onChange('vendor_agent_id', null);
                            this.props.onChange('vendor_agent_name', '');
                            this.props.onChange('purchase_from', formData.hotel_name || '');
                          } else {
                            this.props.onChange('purchase_from', '');
                          }
                        }}
                        style={{ padding: '8px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap', border: active ? '2px solid #1E2D4F' : '1.5px solid #D1D5DB', background: active ? '#1E2D4F' : '#fff', color: active ? '#fff' : '#374151', boxShadow: active ? '0 2px 8px rgba(30,45,79,0.18)' : 'none' }}>
                        {vt === 'Direct' ? '🏨 Direct' : '🤝 Vendor'}
                      </button>
                    );
                  })}
                  {formData.vendor_type === 'Vendor' && (
                    <select value={formData.vendor_agent_id || ''}
                      onChange={e => {
                        const agentId = e.target.value;
                        const agent = this.state.vendorAgents.find(a => String(a.id) === String(agentId));
                        this.props.onChange('vendor_agent_id', agentId || null);
                        this.props.onChange('vendor_agent_name', agent ? agent.agency_name : '');
                        this.props.onChange('purchase_from', agent ? agent.agency_name : '');
                      }}
                      style={{ flex: 1, minWidth: 220, maxWidth: 360, height: 38, border: `1.5px solid ${errors.vendor_agent_id ? '#DC2626' : formData.vendor_agent_id ? '#1E2D4F' : '#D1D5DB'}`, borderRadius: 10, padding: '0 12px', fontFamily: 'Sora,sans-serif', fontSize: 13, color: '#111827', outline: 'none', background: formData.vendor_agent_id ? '#F5F7FF' : '#fff' }}>
                      <option value="">— Select vendor agent —</option>
                      {this.state.vendorAgents.map(a => <option key={a.id} value={a.id}>{a.agency_name}{a.agent_type ? ` (${a.agent_type})` : ''}</option>)}
                    </select>
                  )}
                  {formData.vendor_type === 'Vendor' && formData.vendor_agent_name && <span style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>✓ {formData.vendor_agent_name}</span>}
                  {formData.vendor_type === 'Vendor' && this.state.vendorAgents.length === 0 && <span style={{ fontSize: 11, color: '#D97706' }}>⚠️ No Buyers/Both agents found</span>}
                </div>
                {errors.vendor_agent_id && (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, fontSize: 12, color: '#DC2626', fontWeight: 600 }}>
                    ⚠️ {errors.vendor_agent_id}
                  </div>
                )}
              </div> 
            )}         {/* ← closes isSelected && */}

            {/* ── Check In / Out / Nights ── */}
            <div className="field-group">
              <label>Check In *</label>
              <input ref={this.refs_map.check_in} type="date" name="check_in" value={formData.check_in || ''} onChange={this.handleChange.bind(this)}
                onClick={e => { try { e.target.showPicker(); } catch(_){} }}
                style={{ cursor: 'pointer', ...(errors.check_in ? { borderColor: '#DC2626', boxShadow: '0 0 0 2px rgba(220,38,38,0.15)' } : {}) }} />
              {errors.check_in && <span className="error">{errors.check_in}</span>}
            </div>
            <div className="field-group">
              <label>Check Out *</label>
              <input ref={this.refs_map.check_out} type="date" name="check_out" value={formData.check_out || ''} onChange={this.handleChange.bind(this)}
                onClick={e => { try { e.target.showPicker(); } catch(_){} }}
                style={{ cursor: 'pointer', ...(errors.check_out ? { borderColor: '#DC2626', boxShadow: '0 0 0 2px rgba(220,38,38,0.15)' } : {}) }} />
              {errors.check_out && <span className="error">{errors.check_out}</span>}
            </div>
            <div className="field-group">
              <label>No. of Nights</label>
              <input type="number" name="no_of_nights" value={formData.no_of_nights || 0} readOnly className="bg-gray-50 cursor-not-allowed" onChange={() => {}} />
              <p className="text-xs text-slate-400 mt-1">Auto-calculated from dates</p>
            </div>

            {/* ── Rate Mode ── */}
            <div className="field-group" ref={this.refs_map.mode} style={{ gridColumn: '1 / -1' }}>
              <label style={{ marginBottom: 8, display: 'block' }}>Rate Mode *</label>
              <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                {['Full Input', 'Service Charge'].map(m => {
                  const active = formData.mode === m;
                  return (
                    <button key={m} type="button" onClick={() => this.props.onChange('mode', m)}
                      style={{ padding: '9px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', border: active ? '2px solid #1E2D4F' : '1.5px solid #D1D5DB', background: active ? '#1E2D4F' : '#fff', color: active ? '#fff' : '#374151', boxShadow: active ? '0 2px 8px rgba(30,45,79,0.18)' : 'none' }}>
                      {m}
                    </button>
                  );
                })}
              </div>
              {formData.mode === 'Full Input'     && <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8, padding:'7px 14px', fontSize:13, color:'#1D4ED8' }}><strong>Full Input:</strong> GST = as entered. TAC columns frozen.</div>}
              {formData.mode === 'Service Charge' && <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, padding:'7px 14px', fontSize:13, color:'#15803D' }}><strong>Service Charge:</strong> GST on service charge only.</div>}
              {!formData.mode                     && <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#FEF3C7', border:'1px solid #FDE68A', borderRadius:8, padding:'7px 14px', fontSize:13, color:'#92400E' }}>⚠️ Select a rate mode before adding room types.</div>}
              {errors.mode && <p style={{ fontSize:11, color:'#DC2626', marginTop:6 }}>⚠️ {errors.mode}</p>}
            </div>

            {/* ── Room Type & Pricing ── */}
            <div className="field-group" ref={this.refs_map.room_type_bookings} style={{ gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <label style={{ margin: 0 }}>Room Type &amp; Pricing</label>
                {rtBookings.length > 0 && <span style={{ fontSize: 12, color: '#1E2D4F', fontWeight: 600, background: '#EEF2FF', borderRadius: 20, padding: '2px 10px' }}>{totalRooms} room{totalRooms !== 1 ? 's' : ''} selected</span>}
              </div>

              {!formData.hotel_name && <div style={{ padding: 14, background: '#F9FAFB', border: '1.5px dashed #D1D5DB', borderRadius: 10, fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>Select a hotel above to see available room types</div>}
              {formData.hotel_name && !formData.mode && <div style={{ padding: 14, background: '#FFFBEB', border: '1.5px dashed #FDE68A', borderRadius: 10, fontSize: 13, color: '#92400E', textAlign: 'center' }}>⚠️ Please select a <strong>Rate Mode</strong> above before choosing room types.</div>}
              {formData.hotel_name && formData.mode && loadingRooms && <div style={{ padding: 14, background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 10, fontSize: 13, color: '#0369A1', display: 'flex', alignItems: 'center', gap: 8 }}><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading room types...</div>}
              {formData.hotel_name && formData.mode && !loadingRooms && roomTypesError && <div style={{ padding: 14, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, fontSize: 13, color: '#991B1B' }}>⚠️ Could not load room types.</div>}

              {formData.hotel_name && formData.mode && !loadingRooms && roomTypes.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {roomTypes.map(rt => {
                    const instances = this.getRoomBookingsForType(rt.id);
                    const isActive  = instances.length > 0;

                    return (
                      <div key={rt.id || rt.room_name}
                        style={{ border: isActive ? '2px solid #1E2D4F' : '1.5px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', background: '#fff', boxShadow: isActive ? '0 2px 12px rgba(30,45,79,0.10)' : '0 1px 3px rgba(0,0,0,0.04)', transition: 'all 0.15s' }}>

                        {/* Card header — Add trigger, no shared counter */}
                        <div style={{ display: 'flex', alignItems: 'stretch' }}>
                          <div style={{ width: 120, flexShrink: 0, background: isActive ? '#1E2D4F' : '#F3F4F6', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '12px 8px', gap: 4 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: isActive ? '#93C5FD' : '#9CA3AF', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.3 }}>
                              {(formData.hotel_name || '').split(' ').slice(0, 3).join(' ')}
                            </div>
                            {isActive && (
                              <div style={{ marginTop: 4, background: '#C9A84C', borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 700, color: '#fff' }}>
                                {instances.length} Added
                              </div>
                            )}
                          </div>
                          <div style={{ flex: 1, padding: '12px 16px', borderLeft: `1px solid ${isActive ? '#C7D2FE' : '#E5E7EB'}` }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: isActive ? '#1E2D4F' : '#111827' }}>{rt.room_name}</div>
                            <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#6B7280', marginTop: 4, flexWrap: 'wrap' }}>
                              <span>👥 Max Adults — <strong style={{ color: '#374151' }}>{rt.max_adults}</strong></span>
                              {rt.max_children > 0 && <span>👶 Max Child — <strong style={{ color: '#374151' }}>{rt.max_children}</strong></span>}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderLeft: `1px solid ${isActive ? '#C7D2FE' : '#E5E7EB'}`, background: isActive ? '#F5F7FF' : '#FAFAFA' }}>
                            <button type="button" onClick={() => this.addRoomInstance(rt)}
                              style={{ display: 'flex', alignItems: 'center', gap: 6, height: 34, padding: '0 14px', borderRadius: 8, border: '2px solid #1E2D4F', background: '#1E2D4F', cursor: 'pointer', color: '#fff', fontSize: 12, fontWeight: 700 }}>
                              <Plus size={13} /> Add
                            </button>
                          </div>
                        </div>

                        {/* One full pricing panel per added instance of this room type */}
                        {instances.map((booking, instIdx) => {
                          const roomErr = (errors.room_type_errors || []).find(e => e.room_name === rt.room_name) || {};
                          const isFullInput = formData.mode === 'Full Input';
                          const count = 1; // each instance is exactly one physical room
                          const numInput = (field, val, accent, frozen, placeholder='0') => (
                            <div style={{ position: 'relative' }}>
                              <span style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color: frozen ? '#CBD5E1' : '#9CA3AF', fontSize:12, fontWeight:600 }}>₹</span>
                              <input type="number" min="0" step="0.01" value={val ?? ''} readOnly={frozen}
                                onChange={e => !frozen && this.setField(booking.instance_id, field, e.target.value)}
                                onFocus={e => !frozen && e.target.select()} placeholder={placeholder}
                                style={{ width:'100%', paddingLeft:22, paddingRight:6, height:34,
                                  border:`1.5px solid ${
                                    (field==='purchase_price' && roomErr.purchase_price && !(parseFloat(val)>0)) ? '#FCA5A5' :
                                    (field==='sale_price'     && roomErr.sale_price     && !(parseFloat(val)>0)) ? '#FCA5A5' :
                                    frozen?'#E5E7EB':'#D1D5DB'
                                  }`,
                                  borderRadius:7, fontSize:13, background:frozen?'#F9FAFB':'#fff', color:frozen?'#9CA3AF':'#111827', outline:'none', boxSizing:'border-box', cursor:frozen?'not-allowed':'text' }}
                                onFocusCapture={e => { if(!frozen) e.target.style.borderColor = accent; }}
                                onBlurCapture={e => { if(!frozen) e.target.style.borderColor = '#D1D5DB'; }} />
                              {frozen && <p style={{ fontSize:10, color:'#9CA3AF', marginTop:2, paddingLeft:2 }}>Frozen in Full Input</p>}
                            </div>
                          );
                          const FL = (text, hasErr, msg) => <div style={{ fontSize:10, fontWeight:700, color: hasErr ? '#DC2626' : '#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>{text}{hasErr && <span style={{ marginLeft:4, textTransform:'none' }}>— {msg || 'Required'}</span>}</div>;

                          const tacRoom  = parseFloat(booking?.tac_on_room) || 0;
                          const purBase  = count * nights * (parseFloat(booking?.purchase_price) || 0);  // gross — TAC not included
                          const saleBase = count * nights * (parseFloat(booking?.sale_price) || 0);
                          const extPur   = parseFloat(booking?.extras_amount_purchase) || 0;
                          const extSale  = parseFloat(booking?.extras_amount_sale) || 0;
                          const tacDeduction = tacRoom;
                          // GST is computed the same way regardless of mode — each side
                          // independently, at its own rate.
                          const purGst   = purBase  * ((parseFloat(booking?.purchase_gst_rate) || 0) / 100);
                          const saleGst  = saleBase * ((parseFloat(booking?.gst_rate) || 0) / 100);
                          // Card display: Total = Sub + GST only. TAC is shown as its own
                          // chip for reference, but NOT subtracted from what's displayed here
                          // — the actual TAC deduction happens in the GP/margin calc below.
                          const purTotal = purBase + purGst + extPur;
                          const saleTotal = saleBase + saleGst + extSale;
                          // Real TAC-net purchase total, used only for margin/GP — never displayed as a card badge.
                          // Both modes: ex-GST margin, TAC added back when pre-TAC margin is negative, subtracted otherwise.
                          const gpBase   = (saleBase + extSale) - (purBase + extPur);
                          const margin   = gpBase < 0 ? gpBase + tacDeduction : gpBase - tacDeduction;

                          return (
                            <div key={booking.instance_id}
                              ref={el => { if (el) this.instanceRefs[booking.instance_id] = el; }}
                              style={{ marginTop: instIdx === 0 ? 12 : 16, marginBottom: 12, marginLeft: 12, marginRight: 12, border: '1.5px solid #E0E7FF', borderRadius: 10, background: '#FAFBFF', boxShadow: '0 1px 5px rgba(30,45,79,0.07)', overflow: 'hidden' }}>
                              {/* Instance header — which room this is, plus its own Remove button */}
                              <div style={{ padding:'8px 16px 0', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                                <span style={{ fontSize:12, fontWeight:700, background:'#EEF2FF', color:'#3730A3', borderRadius:20, padding:'3px 12px' }}>
                                  Room {instIdx + 1} of {instances.length} — {nights} night{nights!==1?'s':''}
                                </span>
                                <button type="button" onClick={() => this.removeRoomInstance(booking.instance_id)}
                                  style={{ display:'flex', alignItems:'center', gap:5, height:28, padding:'0 10px', borderRadius:7, border:'1.5px solid #FCA5A5', background:'#FEF2F2', cursor:'pointer', color:'#DC2626', fontSize:11, fontWeight:700 }}>
                                  <Minus size={12} /> Remove
                                </button>
                              </div>

                              {/* ── Occupancy Type + No. of Adults/Children ── */}
                              <div style={{ padding:'10px 16px 0', display:'flex', flexDirection:'column', gap:10 }}>
                                <div style={{ display:'flex', alignItems:'flex-end', gap:16 }}>
                                  <div style={{ flex: 1 }}>
                                  <div style={{ fontSize:10, fontWeight:700, color: roomErr.occupancy_type ? '#DC2626' : '#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>
                                    Occupancy Type * {roomErr.occupancy_type && !booking?.occupancy_type && <span style={{ color:'#DC2626', textTransform:'none' }}>— Enter Occupancy Type</span>}
                                  </div>
                                  <div style={{ display:'flex', gap:8, ...(roomErr.occupancy_type && !booking?.occupancy_type ? { border:'1.5px solid #FCA5A5', borderRadius:8, padding:'4px' } : {}) }}>
                                    {['Single Occupancy', 'Double', 'Triple'].map(ot => {
                                      const active = booking?.occupancy_type === ot;
                                      return (
                                        <button key={ot} type="button" onClick={() => this.setField(booking.instance_id, 'occupancy_type', ot)}
                                          style={{ padding:'7px 18px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', transition:'all 0.15s', whiteSpace:'nowrap', border: active ? '2px solid #1E2D4F' : '1.5px solid #D1D5DB', background: active ? '#1E2D4F' : '#fff', color: active ? '#fff' : '#374151', boxShadow: active ? '0 2px 6px rgba(30,45,79,0.18)' : 'none' }}>
                                          {ot === 'Single Occupancy' ? '🧍 Single' : ot === 'Double' ? '👫 Double' : '🧍‍🧍‍🧍 Triple'}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {booking?.occupancy_type && <p style={{ fontSize:11, color:'#059669', marginTop:4 }}>✓ {booking.occupancy_type}</p>}
                                </div>
                              </div>
                              {/* Adults + Children side by side */}
                              <div style={{ padding:'10px 16px 0' }}>
                                  {/* ── No. of Adults + No. of Children ── */}
                                  <div style={{ display:'flex', gap:10 }}>
                                    {/* Adults */}
                                    <div style={{ flex:1 }}>
                                      <div style={{ fontSize:10, fontWeight:700, color: roomErr.no_of_persons ? '#DC2626' : '#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>
                                        No. of Adults
                                        {booking?.occupancy_type && <span style={{ fontSize:10, color:'#059669', fontWeight:600, textTransform:'none', letterSpacing:0, marginLeft:4 }}>← auto</span>}
                                      </div>
                                      <div style={{ position:'relative' }}>
                                        <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:13 }}>🧑</span>
                                        <input type="number" min="0" max="50" step="1" placeholder="0"
                                          value={booking?.no_of_adults ?? ''}
                                          readOnly={!!booking?.occupancy_type}
                                          onChange={e => !booking?.occupancy_type && this.setField(booking.instance_id, 'no_of_adults', e.target.value)}
                                          style={{ width:'100%', paddingLeft:30, paddingRight:booking?.occupancy_type?28:8, height:36,
                                            border:`1.5px solid ${booking?.occupancy_type ? '#BBF7D0' : '#D1D5DB'}`,
                                            borderRadius:8, fontSize:13,
                                            background: booking?.occupancy_type ? '#F0FDF4' : '#fff',
                                            color: booking?.occupancy_type ? '#065F46' : '#111827',
                                            fontWeight: booking?.occupancy_type ? 700 : 400,
                                            outline:'none', boxSizing:'border-box',
                                            cursor: booking?.occupancy_type ? 'not-allowed' : 'text' }} />
                                        {booking?.occupancy_type && <span style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', fontSize:11 }}>🔒</span>}
                                      </div>
                                    </div>
                                    {/* Children */}
                                    <div style={{ flex:1 }}>
                                      <div style={{ fontSize:10, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>
                                        No. of Children
                                        {(parseInt(booking?.extra_bed_child)||0) > 0 && <span style={{ fontSize:10, color:'#7C3AED', fontWeight:600, textTransform:'none', letterSpacing:0, marginLeft:4 }}>← auto</span>}
                                      </div>
                                      <div style={{ position:'relative' }}>
                                        <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:13 }}>👶</span>
                                        <input type="number" min="0" max="20" step="1" placeholder="0"
                                          value={booking?.no_of_children ?? ''}
                                          readOnly={(parseInt(booking?.extra_bed_child)||0) > 0}
                                          onChange={e => !(parseInt(booking?.extra_bed_child)||0) && this.setField(booking.instance_id, 'no_of_children', e.target.value)}
                                          style={{ width:'100%', paddingLeft:30, paddingRight:(parseInt(booking?.extra_bed_child)||0)>0?28:8, height:36,
                                            border:`1.5px solid ${(parseInt(booking?.extra_bed_child)||0) > 0 ? '#DDD6FE' : '#D1D5DB'}`,
                                            borderRadius:8, fontSize:13,
                                            background: (parseInt(booking?.extra_bed_child)||0) > 0 ? '#F5F3FF' : '#fff',
                                            color: (parseInt(booking?.extra_bed_child)||0) > 0 ? '#5B21B6' : '#111827',
                                            fontWeight: (parseInt(booking?.extra_bed_child)||0) > 0 ? 700 : 400,
                                            outline:'none', boxSizing:'border-box',
                                            cursor: (parseInt(booking?.extra_bed_child)||0) > 0 ? 'not-allowed' : 'text' }} />
                                        {(parseInt(booking?.extra_bed_child)||0) > 0 && <span style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', fontSize:11 }}>🔒</span>}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* ── Meal Plan ── */}
                              <div style={{ padding:'10px 16px 0' }}>
                                <div style={{ fontSize:10, fontWeight:700, color: roomErr.meal_type ? '#DC2626' : '#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>
                                  🍽️ Meal Plan * {roomErr.meal_type && !booking?.meal_type && <span style={{ color:'#DC2626', textTransform:'none' }}>— Enter Meal Type</span>}
                                </div>
                                <div style={{ display:'flex', gap:8, flexWrap:'wrap', ...(roomErr.meal_type && !booking?.meal_type ? { border:'1.5px solid #FCA5A5', borderRadius:8, padding:'6px' } : {}) }}>
                                  {MEAL_PLANS.map(mp => {
                                    const active = booking?.meal_type === mp.code;
                                    return (
                                      <button key={mp.code} type="button"
                                        onClick={() => this.setField(booking.instance_id, 'meal_type', active ? '' : mp.code)}
                                        title={mp.desc}
                                        style={{ padding:'6px 16px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', transition:'all 0.15s', whiteSpace:'nowrap', border: active ? '2px solid #7C3AED' : '1.5px solid #D1D5DB', background: active ? '#7C3AED' : '#fff', color: active ? '#fff' : '#374151', boxShadow: active ? '0 2px 6px rgba(124,58,237,0.2)' : 'none' }}>
                                        <span style={{ fontWeight:800 }}>{mp.code}</span>
                                        <span style={{ fontSize:10, marginLeft:5, opacity:0.85 }}>{mp.desc}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                                {booking?.meal_type && (
                                  <p style={{ fontSize:11, color:'#7C3AED', marginTop:4 }}>
                                    ✓ {booking.meal_type === 'EP' ? 'EP — Room Only Rate' : booking.meal_type === 'CP' ? 'CP — Breakfast Included' : booking.meal_type === 'MAP' ? 'MAP — Breakfast & Lunch/Dinner' : 'AP — Breakfast, Lunch & Dinner'}
                                  </p>
                                )}
                              </div>

                              {/* divider */}
                              <div style={{ margin:'10px 16px 0', borderTop:'1px dashed #E5E7EB' }} />

                              {/* Purchase / Sale */}
                              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}>
                                {/* PURCHASE */}
                                <div style={{ padding:'12px 16px', borderRight:'1.5px solid #E5E7EB' }}>
                                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, paddingBottom:8, borderBottom:'1px solid #DBEAFE' }}>
                                    <span style={{ background:'#1D4ED8', color:'#fff', fontSize:10, fontWeight:800, borderRadius:4, padding:'2px 8px', letterSpacing:'0.08em' }}>PURCHASE</span>
                                    <span style={{ fontSize:11, color:'#6B7280' }}>PURCHASE DETAILS</span>
                                  </div>
                                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                                    <div>{FL('Rate – (Rs.) *', roomErr.purchase_price && !(parseFloat(booking?.purchase_price)>0), 'Enter Purchase Rate')}{numInput('purchase_price', booking?.purchase_price, '#1D4ED8')}</div>
                                    <div>{FL('GST Rate (%)')}
                                      <select value={booking?.purchase_gst_rate ?? 0} onChange={e => this.setField(booking.instance_id, 'purchase_gst_rate', Number(e.target.value))} style={{ width:'100%', height:34, border:'1.5px solid #D1D5DB', borderRadius:7, fontSize:13, padding:'0 8px', background:'#fff' }}>
                                        {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                                      </select>
                                    </div>
                                    <div>{FL('TAC on Room (Rs.)')}{numInput('tac_on_room', booking?.tac_on_room, '#1D4ED8')}</div>
                                    <div>{FL('TAC on Ex. Bed (Rs.)')}{numInput('tac_on_ex_bed', booking?.tac_on_ex_bed, '#1D4ED8')}</div>
                                    <div>{FL('Extras Amount (Rs.)')}{numInput('extras_amount_purchase', booking?.extras_amount_purchase, '#1D4ED8')}</div>
                                    <div>{FL('Extras Details')}<input type="text" value={booking?.extras_details_purchase || ''} onChange={e => this.setField(booking.instance_id, 'extras_details_purchase', e.target.value)} placeholder="Describe extras..." style={{ width:'100%', height:34, border:'1.5px solid #D1D5DB', borderRadius:7, fontSize:12, padding:'0 8px', background:'#fff', boxSizing:'border-box' }} /></div>
                                  </div>
                                  {purBase > 0 && (
                                    <div style={{ marginTop:10, display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                                      <span style={{ fontSize:11, background:'#DBEAFE', color:'#1E40AF', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>Sub: ₹{purBase.toFixed(0)}</span>
                                      <span style={{ fontSize:11, background:'#FEF3C7', color:'#92400E', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>GST: ₹{purGst.toFixed(0)}</span>
                                      {tacRoom > 0 && <span style={{ fontSize:11, background:'#F0FDF4', color:'#166534', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>TAC: -₹{tacDeduction.toFixed(0)}</span>}
                                      {extPur > 0 && <span style={{ fontSize:11, background:'#FFF7ED', color:'#C2410C', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>Extra: ₹{extPur.toFixed(0)}</span>}
                                      <span style={{ fontSize:11, background:'#EFF6FF', color:'#1E40AF', borderRadius:20, padding:'2px 10px', fontWeight:700 }}>Total: ₹{purTotal.toFixed(0)}</span>
                                    </div>
                                  )}
                                </div>
                                {/* SALE */}
                                <div style={{ padding:'12px 16px' }}>
                                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, paddingBottom:8, borderBottom:'1px solid #D1FAE5' }}>
                                    <span style={{ background:'#059669', color:'#fff', fontSize:10, fontWeight:800, borderRadius:4, padding:'2px 8px', letterSpacing:'0.08em' }}>SALE</span>
                                    <span style={{ fontSize:11, color:'#6B7280' }}>SALE RATE DETAILS</span>
                                  </div>
                                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                                    <div>{FL('Rate – (Rs.) *', roomErr.sale_price && !(parseFloat(booking?.sale_price)>0), 'Enter Sale Rate')}{numInput('sale_price', booking?.sale_price, '#059669')}</div>
                                    <div>{FL('GST Rate (%)')}
                                      <select value={booking?.gst_rate ?? 0} onChange={e => this.setField(booking.instance_id, 'gst_rate', Number(e.target.value))} style={{ width:'100%', height:34, border:'1.5px solid #D1D5DB', borderRadius:7, fontSize:13, padding:'0 8px', background:'#fff' }}>
                                        {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                                      </select>
                                    </div>
                                    <div>{FL('Room TAC Passed (Rs.)')}{numInput('room_tac_passed', booking?.room_tac_passed, '#059669', isFullInput)}</div>
                                    <div>{FL('Ex. Bed TAC Passed (Rs.)')}{numInput('ex_bed_tac_passed', booking?.ex_bed_tac_passed, '#059669', isFullInput)}</div>
                                    <div>{FL('Extras Amount (Rs.)')}{numInput('extras_amount_sale', booking?.extras_amount_sale, '#059669')}</div>
                                    <div>{FL('Extras Details')}<input type="text" value={booking?.extras_details_sale || ''} onChange={e => this.setField(booking.instance_id, 'extras_details_sale', e.target.value)} placeholder="Describe extras..." style={{ width:'100%', height:34, border:'1.5px solid #D1D5DB', borderRadius:7, fontSize:12, padding:'0 8px', background:'#fff', boxSizing:'border-box' }} /></div>
                                  </div>
                                  {saleBase > 0 && (
                                    <div style={{ marginTop:10, display:'flex', gap:6, flexWrap:'wrap' }}>
                                      <span style={{ fontSize:11, background:'#D1FAE5', color:'#065F46', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>Sub: ₹{saleBase.toFixed(0)}</span>
                                      <span style={{ fontSize:11, background:'#FEF3C7', color:'#92400E', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>GST: ₹{saleGst.toFixed(0)}</span>
                                      {extSale > 0 && <span style={{ fontSize:11, background:'#FFF7ED', color:'#C2410C', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>Extra: ₹{extSale.toFixed(0)}</span>}
                                      <span style={{ fontSize:11, background:'#ECFDF5', color:'#065F46', borderRadius:20, padding:'2px 10px', fontWeight:700 }}>Total: ₹{saleTotal.toFixed(0)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Extra Beds */}
                              <div style={{ borderTop:'1.5px solid #E5E7EB', padding:'12px 16px', background:'#FAFBFF' }}>
                                <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'#6B7280', marginBottom:10 }}>🛏️ Extra Beds for this Room Type</div>
                                {[
                                  { label:'Child (5–12 Yrs)', emoji:'👶', cntKey:'extra_bed_child', purKey:'ex_bed_rate_child_purchase', saleKey:'ex_bed_rate_child_sale', gstKey:'extra_bed_child_gst' },
                                  { label:'Adult (12 Yrs+)',  emoji:'🧑', cntKey:'extra_bed_adult', purKey:'ex_bed_rate_purchase',       saleKey:'ex_bed_rate_sale',       gstKey:'extra_bed_adult_gst' },
                                ].map((eb, ei) => {
                                  const ebCount    = parseInt(booking?.[eb.cntKey]) || 0;
                                  const ebSaleRate = parseFloat(booking?.[eb.saleKey]) || 0;
                                  const ebGst      = parseFloat(booking?.[eb.gstKey] ?? 0);
                                  const ebUnits    = ebCount * nights;
                                  const ebSaleBase = ebUnits * ebSaleRate;
                                  const ebGstAmt   = ebSaleBase * ebGst / 100;
                                  const ebSaleTotal = ebSaleBase + ebGstAmt;
                                  return (
                                    <div key={eb.cntKey} style={{ display:'grid', gridTemplateColumns:'180px 1fr 1fr 100px 90px', gap:8, alignItems:'end', marginBottom: ei === 0 ? 10 : 0 }}>
                                      <div>
                                        <div style={{ fontSize:10, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>{eb.emoji} {eb.label}</div>
                                        <div style={{ display:'flex', alignItems:'center', gap:8, height:34 }}>
                                          <button type="button" onClick={() => this.setField(booking.instance_id, eb.cntKey, Math.max(0, ebCount - 1))} disabled={ebCount === 0}
                                            style={{ width:26, height:26, borderRadius:'50%', border:'2px solid #D1D5DB', background:'#fff', cursor:ebCount===0?'not-allowed':'pointer', color:'#6B7280', opacity:ebCount===0?0.3:1, fontSize:14, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>−</button>
                                          <span style={{ width:22, textAlign:'center', fontWeight:800, fontSize:16, color:ebCount>0?'#7C3AED':'#CBD5E1' }}>{ebCount}</span>
                                          <button type="button" onClick={() => this.setField(booking.instance_id, eb.cntKey, ebCount + 1)}
                                            style={{ width:26, height:26, borderRadius:'50%', border:'2px solid #7C3AED', background:'#7C3AED', cursor:'pointer', color:'#fff', fontSize:14, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>+</button>
                                          {ebCount > 0 && <span style={{ fontSize:10, color:'#9CA3AF' }}>×{nights}n={ebUnits}</span>}
                                        </div>
                                      </div>
                                      <div>
                                        <div style={{ fontSize:10, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>Purchase Rate / Bed / Night</div>
                                        <div style={{ position:'relative' }}>
                                          <span style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:'#9CA3AF', fontSize:12, fontWeight:600 }}>₹</span>
                                          <input type="number" min="0" step="0.01" value={booking?.[eb.purKey] ?? 0} onChange={e => this.setField(booking.instance_id, eb.purKey, e.target.value)} onFocus={e => e.target.select()} disabled={ebCount === 0}
                                            style={{ width:'100%', paddingLeft:20, paddingRight:6, height:34, border:'1.5px solid #D1D5DB', borderRadius:7, fontSize:13, background:ebCount===0?'#F9FAFB':'#fff', color:ebCount===0?'#9CA3AF':'#111827', outline:'none', boxSizing:'border-box', cursor:ebCount===0?'not-allowed':'text' }} />
                                        </div>
                                      </div>
                                      <div>
                                        <div style={{ fontSize:10, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>Sale Rate / Bed / Night</div>
                                        <div style={{ position:'relative' }}>
                                          <span style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:'#9CA3AF', fontSize:12, fontWeight:600 }}>₹</span>
                                          <input type="number" min="0" step="0.01" value={booking?.[eb.saleKey] ?? 0} onChange={e => this.setField(booking.instance_id, eb.saleKey, e.target.value)} onFocus={e => e.target.select()} disabled={ebCount === 0}
                                            style={{ width:'100%', paddingLeft:20, paddingRight:6, height:34, border:'1.5px solid #D1D5DB', borderRadius:7, fontSize:13, background:ebCount===0?'#F9FAFB':'#fff', color:ebCount===0?'#9CA3AF':'#111827', outline:'none', boxSizing:'border-box', cursor:ebCount===0?'not-allowed':'text' }} />
                                        </div>
                                      </div>
                                      <div>
                                        <div style={{ fontSize:10, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>GST %</div>
                                        <select value={booking?.[eb.gstKey] ?? 0} onChange={e => this.setField(booking.instance_id, eb.gstKey, Number(e.target.value))} disabled={ebCount === 0}
                                          style={{ width:'100%', height:34, border:'1.5px solid #D1D5DB', borderRadius:7, fontSize:13, padding:'0 6px', background:ebCount===0?'#F9FAFB':'#fff', color:ebCount===0?'#9CA3AF':'#111827', cursor:ebCount===0?'not-allowed':'pointer' }}>
                                          {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                                        </select>
                                      </div>
                                      <div>
                                        <div style={{ fontSize:10, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>Sale Total</div>
                                        <div style={{ height:34, display:'flex', alignItems:'center', justifyContent:'flex-end', paddingRight:8, background:ebSaleTotal>0?'#F5F3FF':'#F9FAFB', border:`1px solid ${ebSaleTotal>0?'#DDD6FE':'#E5E7EB'}`, borderRadius:7, fontSize:13, fontWeight:800, color:ebSaleTotal>0?'#5B21B6':'#CBD5E1' }}>
                                          {ebSaleTotal > 0 ? `₹${ebSaleTotal.toFixed(0)}` : '—'}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Margin footer */}
                              {purBase > 0 && saleBase > 0 && (
                                <div style={{ margin:'0 16px 12px', padding:'8px 14px', borderRadius:8, background:margin>=0?'#F0FDF4':'#FEF2F2', border:`1px solid ${margin>=0?'#BBF7D0':'#FECACA'}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                                  <span style={{ fontSize:12, color:margin>=0?'#166534':'#991B1B', fontWeight:600 }}>
                                    Margin (ex-GST):
                                  </span>
                                  <span style={{ fontSize:15, fontWeight:800, color:margin>=0?'#15803D':'#DC2626' }}>{margin>=0?'+':''}₹{margin.toFixed(2)}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Calculation Preview */}
              {rtBookings.length > 0 && (() => {
                const gpPositive = grossProfit >= 0;
                const allPriced  = rtBookings.every(b => parseFloat(b.sale_price) > 0 && parseFloat(b.purchase_price) > 0);
                return (
                  <div style={{ marginTop:14, borderRadius:12, overflow:'hidden', border:`1px solid ${gpNegative?'#FECACA':'#E0E7FF'}` }}>
                    <div style={{ background:'#FFF8E7', borderBottom:'1px solid #FDE68A', padding:'10px 14px', display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <span style={{ fontSize:16 }}>⚠️</span>
                      <span style={{ fontSize:13, fontWeight:700, color:'#92400E' }}>Calculation uses values from this step:</span>
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                        <span style={{ fontSize:11, background:'#EEF2FF', color:'#3730A3', borderRadius:20, padding:'2px 10px', fontWeight:700 }}>Nights: {nights}</span>
                        {Object.entries(rtBookings.reduce((acc, b) => { acc[b.room_name] = (acc[b.room_name] || 0) + (b.count || 0); return acc; }, {})).map(([name, cnt]) => (
                          <span key={name} style={{ fontSize:11, background:cnt>0?'#DCFCE7':'#FEE2E2', color:cnt>0?'#166534':'#991B1B', borderRadius:20, padding:'2px 10px', fontWeight:700 }}>
                            {name}: {cnt}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div style={{ background:'#fff' }}>
                      {rtBookings.map((b, idx) => {
                        const t  = calcRoomTotals(b, nights);
                        const eb = calcExtraBedTotals(b, nights);
                        const hasPrices = parseFloat(b.sale_price) > 0;
                        const rowBg = idx % 2 === 1 ? '#FAFBFF' : '#fff';
                        const COL = '1fr 75px 65px 80px 75px 65px 82px';
                        const cell = (label, val, color='#374151', lc='#9CA3AF') => (
                          <div style={{ padding:'8px 8px', textAlign:'right', borderLeft:'1px solid #F1F5F9' }}>
                            <div style={{ fontSize:9, color:lc, textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</div>
                            <div style={{ fontSize:11, fontWeight:700, color }}>{val}</div>
                          </div>
                        );
                        const ebCell = (label, val, color='#92400E', lc='#B45309') => (
                          <div style={{ padding:'7px 8px', textAlign:'right', borderLeft:'1px solid #FDE68A' }}>
                            <div style={{ fontSize:9, color:lc, textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</div>
                            <div style={{ fontSize:11, fontWeight:700, color }}>{val}</div>
                          </div>
                        );
                        const dash = <span style={{ color:'#D1D5DB' }}>—</span>;
                        return (
                          <React.Fragment key={b.instance_id}>
                            {/* Room row */}
                            <div style={{ display:'grid', gridTemplateColumns:COL, borderBottom:'1px solid #F1F5F9', background:rowBg }}>
                              <div style={{ padding:'9px 12px', fontSize:11, color:'#374151', display:'flex', alignItems:'center', gap:5 }}>
                                <span style={{ fontWeight:600 }}>{b.room_name}</span>
                                {b.meal_type && <span style={{ background:'#F5F3FF', color:'#6D28D9', border:'1px solid #DDD6FE', borderRadius:8, padding:'1px 6px', fontSize:9, fontWeight:700 }}>🍽️{b.meal_type}</span>}
                                <span style={{ color:'#9CA3AF', fontSize:10 }}>{b.count}rm×{nights}nt</span>
                              </div>
                              {cell('Pur ex-GST',  hasPrices ? `₹${t.purBase.toFixed(0)}`  : dash, '#1E40AF')}
                              {cell('Pur GST',     hasPrices ? `₹${t.purGst.toFixed(0)}`   : dash, '#C2410C')}
                              {cell('Pur Total',   hasPrices ? `₹${t.purTotal.toFixed(0)}`  : dash, '#1D4ED8', '#6B7280')}
                              {cell('Sale ex-GST', hasPrices ? `₹${t.saleBase.toFixed(0)}` : dash, '#065F46')}
                              {cell('Sale GST',    hasPrices ? `₹${t.saleGst.toFixed(0)}`  : dash, '#C2410C')}
                              {cell('Sale Total',  hasPrices ? `₹${t.saleTotal.toFixed(0)}`: dash, '#1E2D4F', '#6B7280')}
                            </div>
                            {/* Extra bed row */}
                            {eb.hasAny && (
                              <div style={{ display:'grid', gridTemplateColumns:COL, borderBottom:'1px solid #F1F5F9', background:'#FFFBEB' }}>
                                <div style={{ padding:'7px 12px 7px 24px', fontSize:10, color:'#92400E', display:'flex', alignItems:'center', gap:4 }}>
                                  🛏️ <span style={{ fontWeight:600 }}>Extra Bed</span>
                                  <span style={{ color:'#B45309', fontSize:9 }}>
                                    {(parseInt(b.extra_bed_child)||0)>0?` ${b.extra_bed_child}child`:''}
                                    {(parseInt(b.extra_bed_adult)||0)>0?` ${b.extra_bed_adult}adult`:''}
                                    {` ×${nights}nt`}
                                  </span>
                                </div>
                                {ebCell('Pur ex-GST', `₹${eb.purBase.toFixed(0)}`)}
                                {ebCell('Pur GST',    `₹${eb.purGst.toFixed(0)}`)}
                                {ebCell('Pur Total',  `₹${eb.purTotal.toFixed(0)}`)}
                                {ebCell('Sale ex-GST',`₹${eb.saleBase.toFixed(0)}`, '#065F46', '#059669')}
                                {ebCell('Sale GST',   `₹${eb.saleGst.toFixed(0)}`,  '#C2410C', '#B45309')}
                                {ebCell('Sale Total', `₹${eb.saleTotal.toFixed(0)}`, '#92400E', '#B45309')}
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                    {/* Footer totals */}
                    <div style={{ background:'#1E2D4F', padding:'10px 12px', display:'grid', gridTemplateColumns:'1fr 75px 65px 80px 75px 65px 82px', alignItems:'center' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:10, color:'#94A3B8' }}>
                          GP (ex-GST):
                        </span>
                        {allPriced ? (
                          <span style={{ fontSize:13, fontWeight:800, color:gpPositive?'#4ADE80':'#F87171', background:gpPositive?'rgba(74,222,128,0.12)':'rgba(248,113,113,0.12)', borderRadius:20, padding:'2px 10px' }}>
                            {gpPositive?'+':''}₹{grossProfit.toFixed(2)}
                          </span>
                        ) : <span style={{ fontSize:11, color:'#475569', fontStyle:'italic' }}>Enter prices above</span>}
                      </div>
                      {[
                        ['Pur ex-GST', grandPurchase, '#93C5FD'],
                        ['Pur GST',    grandPurGst,   '#FBD38D'],
                        ['Pur Total',  grandPurTotal,  '#CBD5E1'],
                        ['Sale ex-GST',grandSaleBase, '#6EE7B7'],
                        ['Sale GST',   grandGST,      '#FBD38D'],
                        ['Total Sale', grandTotal,    '#fff'],
                      ].map(([lbl, val, clr]) => (
                        <div key={lbl} style={{ textAlign:'right' }}>
                          <div style={{ fontSize:8, color:'#64748B', textTransform:'uppercase', letterSpacing:'0.05em' }}>{lbl}</div>
                          <div style={{ fontSize:lbl==='Total Sale'?14:12, fontWeight:lbl==='Total Sale'?800:700, color:clr }}>₹{val.toFixed(0)}</div>
                        </div>
                      ))}
                    </div>
                    {gpNegative && (
                      <div ref={this.refs_map.negative_reason} style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderTop:'2px solid #EF4444', padding:'14px 16px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                          <span style={{ fontSize:18 }}>🔴</span>
                          <div>
                            <div style={{ fontSize:13, fontWeight:700, color:'#991B1B' }}>Negative Gross Profit — Admin Approval Required</div>
                            <div style={{ fontSize:11, color:'#B91C1C', marginTop:2 }}>GP is <strong>₹{grossProfit.toFixed(2)}</strong>. This booking will go to <strong>Pending Approval</strong> status.</div>
                          </div>
                        </div>
                        <textarea value={formData.negative_reason || ''} onChange={e => this.props.onChange('negative_reason', e.target.value)}
                          placeholder="Reason for negative profit (required for Admin approval)..."
                          rows={3} style={{ width:'100%', border:'1.5px solid #FECACA', borderRadius:8, padding:'10px 12px', fontSize:13, color:'#111827', background:'#fff', resize:'vertical', outline:'none', boxSizing:'border-box' }}
                          onFocusCapture={e => { e.target.style.borderColor = '#EF4444'; }}
                          onBlurCapture={e => { e.target.style.borderColor = '#FECACA'; }} />
                        {!formData.negative_reason?.trim() && <p style={{ fontSize:11, color:'#DC2626', marginTop:4 }}>⚠️ Enter response for negative GP before proceeding.</p>}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* No rooms warning */}
              {formData.hotel_name && formData.mode && !loadingRooms && rtBookings.length === 0 && (
                <p ref={this.refs_map.no_rooms_warning} style={{ marginTop:8, fontSize:12, color:'#D97706', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, padding:'6px 12px', display:'inline-block' }}>
                  ⚠️ Add at least one room type using the + button above.
                </p>
              )}

              {/* Room validation error summary */}
              {errors.room_type_bookings && typeof errors.room_type_bookings === 'string' && (
                <div style={{ marginTop: 10, padding: '10px 14px', background: '#FEF2F2', border: '1.5px solid #FECACA', borderRadius: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#991B1B', marginBottom: 4 }}>⚠️ Please complete required fields in all rooms:</div>
                  {errors.room_type_bookings.split(' | ').map((msg, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#DC2626', padding: '2px 0' }}>• {msg}</div>
                  ))}
                </div>
              )}

              {/* Per-room inline error pill list */}
              {errors.room_type_errors && errors.room_type_errors.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {errors.room_type_errors.map((re, i) => (
                    <div key={i} style={{ padding: '8px 12px', background: '#FFF5F5', border: '1px solid #FECACA', borderRadius: 8, fontSize: 11 }}>
                      <strong style={{ color: '#991B1B' }}>{re.room_name}:</strong>
                      <span style={{ color: '#DC2626', marginLeft: 8 }}>
                        {[
                          re.occupancy_type && '🧍 Enter Occupancy Type',
                          re.no_of_persons  && '👤 Enter No. of Persons',
                          re.meal_type      && '🍽️ Enter Meal Type',
                          re.purchase_price && '₹ Enter Purchase Rate',
                          re.sale_price     && '₹ Enter Sale Rate',
                        ].filter(Boolean).join('  ·  ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            </div>
            <input type="hidden" name="single_rooms"    value={formData.single_rooms    || 0} />
            <input type="hidden" name="double_rooms"    value={formData.double_rooms    || 0} />
            <input type="hidden" name="extra_bed_child" value={formData.extra_bed_child || 0} />
            <input type="hidden" name="extra_bed_adult" value={formData.extra_bed_adult || 0} />
        
        </section>
      </div>
    );
  }
}

export default HotelDetails;